import React, { useMemo, useState } from "react";
import { Container, Card, Icon, Modal, Image } from "semantic-ui-react";
import { useNavigate } from "react-router-dom";
import { listedTools as toolRegistry } from "../tools/registry";
import "../App.css";

/* ------------------------------------------------------------------ */
/*  The index: everything I've built, in one browsable, shareable page */
/* ------------------------------------------------------------------ */

// Paths served by the SPA router. Everything else under /projects/* is a
// separately built static app, so it needs a real page load rather than a
// client-side route.
const isSpaRoute = (link) =>
  ["/tools/", "/expense-analyzer", "/doom-life-game"].some((p) => link.startsWith(p));

// ── Apps: real accounts, real users, live data ───────────────────────────────
const apps = [
  {
    title: "📸 Moment Capture",
    description:
      "Mobile-first PWA for capturing and curating daily moments into video compilations. Google Auth, camera capture, review queue, gallery, and FFmpeg-powered export — all on Firebase.",
    tech: ["React", "Vite", "Firebase", "Tailwind CSS", "FFmpeg", "PWA"],
    link: "/projects/moments",
  },
  {
    title: "🗃️ Collector Shop",
    description:
      "Shared inventory tracker for sports cards, comics, and memorabilia. Add items in one tap (name and you're done), then fill in price paid, category, grading, tags, and photos later. Log sales, check recent eBay sold comps, and watch a live dashboard of cost basis, profit, and margins. Two collaborators share one shop with an invite code.",
    tech: ["React", "Vite", "Firebase", "Firestore", "Storage", "Tailwind CSS"],
    link: "/projects/collector",
  },
  {
    title: "🏌️ Swing Coach",
    description:
      "Two sports, three tools. The analyzer points a camera at a swing and a pose model reads the body 30 times a second: stance width, knee bend, spine angle, how far the head travels, whether the hips start the swing before the shoulders, and whether you finish on balance. It talks you through the fixes out loud, or breaks down an uploaded clip frame by frame, and never uploads the video. The golf side adds a GPS caddy: mark your ball and the flag on a satellite map, trace a dogleg if the hole bends, and it reads the distance, checks the terrain height and picks a club with a reason. Every shot you log teaches it how far you actually hit that club, so the estimates it starts with get replaced by your own numbers.",
    tech: ["JavaScript", "MediaPipe BlazePose", "WebAssembly", "Leaflet", "Geolocation", "Firebase Auth", "Firestore", "Web Speech API"],
    link: "/projects/swing-coach",
  },
  {
    title: "📖 Workbook Reader",
    description:
      "A reading aid for a kid who's still learning. Snap a photo of a workbook page and AI rebuilds the questions and directions on screen — then tapping any word reads it aloud. Spoken-word audio is cached in a shared, cross-family bank so common words play instantly and never regenerate, with an on-device voice fallback for offline or older devices. Grown-ups sign in and manage a profile per child; the child does the work on paper.",
    tech: ["React", "Vite", "Firebase", "Gemini Vision", "Cloud TTS", "PWA"],
    link: "/projects/workbook",
  },
  {
    title: "🎮 PSX Station",
    description:
      "A PlayStation 1 emulator in a browser tab. Drop in a disc image you own — .7z, .bin/.cue, .chd — and it plays, with gamepad and touch controls. Discs stay on your device because they're enormous; the save states and memory cards sync to your account, so you can put a game down on the desktop and pick it back up on a phone.",
    tech: ["JavaScript", "WebAssembly", "EmulatorJS", "IndexedDB", "Firebase Auth", "Cloud Storage", "PWA"],
    link: "/projects/psx",
  },
  {
    title: "🎼 Solra Trainer",
    description:
      "A practice tool for a constructed language whose phonemes are pure tones instead of vowels and consonants. Ear training against the language's own pitch reference, minimal-pair drills inside a word family, a searchable 192-word dictionary, a sentence composer that runs the spec's deterministic validator, and a microphone decoder that reads the tones back with Goertzel filters. Every sound is synthesised in the browser; nothing is recorded audio.",
    tech: ["JavaScript", "Web Audio", "AudioWorklet", "Goertzel DSP", "Spaced repetition", "PWA"],
    link: "/projects/solra",
  },
  {
    title: "⚾ Playball",
    description:
      "Walk-up music manager for game day. Pull in Spotify playlists, set start/stop times per song, assign players to songs, and drag the batting order — the playlist re-syncs to Spotify automatically.",
    tech: ["JavaScript", "Spotify API", "Firebase Auth", "Firestore", "PWA"],
    link: "/projects/playball",
  },
  {
    title: "🚽 CanITwo",
    description:
      "Community bathroom finder for travelers. See nearby gas stations, stores, and cafes on a map, report whether they have a toilet, rate and review it, and launch directions when nature calls.",
    tech: ["JavaScript", "Leaflet", "OpenStreetMap", "Firebase Auth", "Firestore", "PWA"],
    link: "/projects/canitwo",
  },
  {
    title: "🗃️ Recipe Box",
    description:
      "The family recipe box, digitized. Write recipes onto simple cards, attach photos or videos of the people who make them, connect with family, and hand cards — or your whole box — to each other. No blogs, no ads, just the cards. Also lives at gramandpops.com.",
    tech: ["JavaScript", "Firebase Auth", "Firestore", "Storage", "PWA"],
    link: "/projects/recipebox",
  },
  {
    title: "✉️ Electronic Mail",
    description:
      "An iMessage-styled Gmail client with Google OAuth, thread-based chat UI, and Firestore-backed email caching for fast loads.",
    tech: ["React", "Firebase", "Google OAuth", "Gmail API"],
    link: "/projects/electronic-mail",
  },
];

// ── Tools: rendered from the registry so this index can't drift ──────────────
// Adding a tool to src/tools/registry.js puts it here and at /tools with no
// edit to either page.
const registryTools = toolRegistry.map((t) => ({
  title: t.title,
  description: t.description,
  tech: t.tech || ["React"],
  link: `/tools/${t.slug}`,
}));

const tools = [
  ...registryTools,
  {
    title: "💳 Expense Analyzer",
    description:
      "Upload a CSV of transactions and instantly get AI-powered categorization, spending breakdowns, and visual analytics to understand your expenses.",
    tech: ["React", "JavaScript", "Data Visualization"],
    link: "/expense-analyzer",
  },
];

// ── Games, toys and experiments ──────────────────────────────────────────────
const playthings = [
  {
    title: "🎟️ Ticket Book: Disney Trivia",
    description:
      "Two Disney games behind one switch. Trivia is a book of 248 tickets on two shelves, films and characters plus a deep Walt Disney World section, dealt by topic or by which park they live in; tap a ticket to flip it, with sound effects synthesised in the browser. Where Am I? is a private photo hunt: make a deck, hand out its password, and that pair is the only way in. Each round opens a peephole onto a photo that three taps widen, then you drop a pin on the deck's own map to say where it was taken.",
    tech: ["JavaScript", "Firebase", "Firestore", "Cloud Storage", "PBKDF2", "Web Audio", "CSS masks"],
    link: "/projects/disney-trivia",
  },
  {
    title: "💀 Dead Net",
    description:
      "Dead Internet Theory as a Grateful Dead fan forum: every thread, reply and reaction is posted by a bot, and you're the only human in the room.",
    tech: ["JavaScript", "HTML5", "Canvas API"],
    link: "/projects/deadnet",
  },
  {
    title: "🕰️ Binary Flip Counter",
    description:
      "A wooden split-flap clock that counts in base 2. Hours, minutes and seconds each get a tray of flipping tiles, with the decimal value read out alongside so you can check your own binary math.",
    tech: ["JavaScript", "CSS 3D transforms", "HTML5"],
    link: "/projects/binary",
  },
  {
    title: "🔐 Cam's Secret Decoder",
    description:
      "A retro-styled cipher tool for encoding and decoding secret messages. Features a custom A–Z substitution cipher with a full reference table.",
    tech: ["JavaScript", "HTML5", "CSS3"],
    link: "/projects/camcoded",
  },
  {
    title: "🔍 Spelling Detective",
    description:
      "Interactive spelling practice app with pattern sorting, look-cover-write-check drills, tricky letter identification, and custom word lists.",
    tech: ["JavaScript", "HTML5", "CSS3"],
    link: "/projects/writer",
    images: [{ src: require("../assets/writersImage.png"), alt: "Spelling Detective app screenshot" }],
  },
  {
    title: "🪨 Boulder Smash",
    description:
      "A 3D smash-em-up built for kids. Boulder the green giant stomps through four cities: punch buildings until they wobble and crash, bonk Dr. Fizzle's gang into the sky, and grab rubble or a whole building and throw it. People trapped behind rock piles and steel pens need rescuing (steel only gives way to a thrown building), and each city ends with a boss: Grumble the giant, Rocko who rolls boulders you punch back at him, and Bruno the bulldozer who has to crash before he can be hurt. Aim assist and an on-screen controller make it playable on a phone, and a laptop can steer the stick with the trackpad.",
    tech: ["JavaScript", "Three.js", "WebGL", "Web Audio", "Pointer Lock API"],
    link: "/projects/boulder-smash",
  },
  {
    title: "🎮 Camboy",
    description:
      "A pocket game player from Cambo Industries, named for Cam and Bo. Flip through the cartridge book, tap a game, and watch it slide into the back of the Camboy before it turns around and boots up. It has a joystick under the D-pad so a sliding thumb never stalls, haptic taps, contrast and volume wheels, and it installs to a phone's home screen. Cartridge #1 is Sweat Boy: you're the court mopper at a pro basketball game, scrubbing every sweat puddle before the players come back up the floor. Every slip costs your team the shot, and a spotless floor means they go for three. Win to unlock the next of five venues, from the Rec Center to the Finals, and grab power-ups that come and go: a Jumbo Mop for one trip, Fast Kicks until someone slips, and a Timeout for three more seconds. Cartridge #2 is Boulder Smash, a side-scrolling 2D take on the 3D game: punch buildings down, bonk Dr. Fizzle's gang, free trapped people, and beat Grumble, Rocko, Bruno and Dr. Fizzle. Jump and smash to leap, jump and press down to slam, and leap then slam for a super slam. Cartridge #3 is Dark Hat: Sorcerer's Escape, Campbell's game: fall into an evil magician's top hat, learn magic tricks from trapped spirits (pick one of two paths for each, and you're stuck with it for the run), and fight down to the Great Malvado before your soul runs out. The chiptune music, crowds and crunches are all synthesized in the browser.",
    tech: ["JavaScript", "Canvas API", "Web Audio", "CSS 3D transforms", "PWA"],
    link: "/projects/camboy",
  },
  {
    title: "🧩 Camboku",
    description:
      "A logic puzzle starring Cam and Bo, with no ads. Put one face in every row, every column and every colored region, and never let two faces touch, not even at the corners. Every board is generated on the spot: the generator lays down an answer, grows the regions out from it, then reshapes the borders until that answer is the only one. A built-in solver then works the board the way a person would, and the level is kept only if it can be finished by logic alone. The hardest rule it needed sets the level's difficulty, from 5×5 warm ups to 9×9 boards that need pigeonhole reasoning. Wrong taps cost one of three hearts, Hint points at the next thing you can work out and says why, and there's a new 8×8 board every day.",
    tech: ["JavaScript", "Procedural generation", "Constraint solving", "PWA"],
    link: "/projects/camboku",
  },
  {
    title: "🎩 Tophat & Spears",
    description:
      "A co-op RTS and FPS in one match, for up to five friends. One player is the Tophat, commanding three squads of marines from above as a swarm pours over the ridge. Up to four Spears drop into those marines in first person and hop from body to body. The Tophat can leash a Spear to a zone; miss the deadline and you bleed. Everyone types the same password to land in the same lobby. One player's browser runs the battle and streams it to the rest through Firebase, and if that player leaves, the next one picks the match up where it was.",
    tech: ["JavaScript", "Three.js", "WebGL", "Firebase Realtime Database", "PBKDF2", "Pointer Lock API"],
    link: "/projects/tophat-spears",
  },
  {
    title: "👋 Party Room",
    description:
      "The party games that kept everyone together on video calls in 2020, rebuilt for the family: all five, Word Race included. One person starts a room and reads out a five-letter code; everyone else joins on their own phone. Forehead: hold the phone to your forehead and nod down when you get the word, tip up to pass, while every other phone shows the word so the people on the call can play too. Trivia: the same question on every phone, with points for right and for fast. Quick Sketch: one person draws on their screen, everyone else watches it appear and races to type the answer, and a near miss gets a private \"so close!\". Chips & Dip: two chips on the table, play the dip from your hand that goes with both, and the judge picks a favorite. The host's phone runs the game and keeps answers, hands and the secret word out of everyone else's reach; if it drops out, the next phone carries on. Word Race: eight letters on a wheel and ninety seconds to swipe out every word you can, with a bonus for being first to a five, six, seven or eight-letter word and a crown for whoever wins the round. Forehead also works on one phone with no room at all.",
    tech: ["JavaScript", "Firebase Realtime Database", "Canvas API", "Device Orientation", "Web Audio"],
    link: "/projects/party-room",
  },
  {
    title: "👾 Conway's DOOM",
    description:
      "Conway's Game of Life meets first-person shooter. Navigate an evolving cellular automata maze, shoot walls, manage ammo, and survive progressive difficulty levels.",
    tech: ["JavaScript", "Three.js", "WebGL"],
    link: "/doom-life-game",
  },
  {
    title: "🎨 Color Chasers",
    description:
      "Multiplayer color-based strategy game where players compete to capture territory on a dynamic grid in real time.",
    tech: ["React", "WebSocket", "Game Development"],
    images: [
      { src: require("../assets/colorchasers1.png"), alt: "Color Chasers early game" },
      { src: require("../assets/colorchasers2.png"), alt: "Color Chasers advanced game" },
    ],
  },
];

// ── Lives somewhere else ─────────────────────────────────────────────────────
const elsewhere = [
  {
    title: "🏋️ Weight Challenge Bros",
    description:
      "A collaborative weight-loss tracker for friends with interactive charts and friendly competition.",
    tech: ["React", "Chart.js", "Firebase"],
    link: "https://weightchallengebros.com/home",
    images: [{ src: require("../assets/weight_challenge.png"), alt: "Weight tracking dashboard" }],
  },
  {
    title: "⌚ Never Late — Fitbit Clock Face",
    description:
      "Randomizes the displayed time up to 15 minutes fast, creating healthy uncertainty to keep you punctual.",
    tech: ["Fitbit SDK", "JavaScript"],
    link: "https://gallery.fitbit.com/details/515c8da3-82a8-4aaf-8446-587398ef4cdb",
  },
  {
    title: "🏀 NBA Player Performance Predictor",
    description:
      "LSTM/RNN neural network app that predicts NBA player props. Automated box score scraping for model retraining, with interactive Streamlit dashboards.",
    tech: ["Python", "LSTM/RNN", "Streamlit", "Web Scraping"],
    images: [
      { src: require("../assets/nba_prediction.png"), alt: "Player prediction interface" },
      { src: require("../assets/nba_team_stats.png"), alt: "Team statistics dashboard" },
      { src: require("../assets/nba_player_chart.png"), alt: "Player performance trend" },
    ],
  },
  {
    title: "📱 Mobile App",
    description:
      "My first mobile application, currently in internal testing and coming soon to iOS and Android.",
    tech: ["React Native"],
    comingSoon: true,
  },
];

const SECTIONS = [
  { key: "apps", label: "Apps", blurb: "Sign in, save your stuff, come back to it.", items: apps },
  {
    key: "tools",
    label: "Tools",
    blurb: "Single-purpose things that do one job in the browser.",
    items: tools,
  },
  {
    key: "play",
    label: "Games & experiments",
    blurb: "Built for the fun of building them.",
    items: playthings,
  },
  {
    key: "elsewhere",
    label: "Elsewhere",
    blurb: "Lives on another host, or isn't a website at all.",
    items: elsewhere,
  },
];

const TOTAL = SECTIONS.reduce((n, s) => n + s.items.length, 0);

// Every whitespace-separated term has to appear somewhere in the card, so
// "firebase game" narrows rather than widens.
const matches = (item, q) => {
  if (!q) return true;
  const hay = `${item.title} ${item.description} ${item.tech.join(" ")}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => hay.includes(term));
};

const Projects = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("all");
  const navigate = useNavigate();

  const go = (link) => {
    if (!link) return;
    if (link.startsWith("http")) window.open(link, "_blank", "noopener");
    else if (isSpaRoute(link)) navigate(link);
    else window.location.href = link; // separately built static app
  };

  const visible = useMemo(
    () =>
      SECTIONS.filter((s) => section === "all" || s.key === section)
        .map((s) => ({ ...s, items: s.items.filter((i) => matches(i, query)) }))
        .filter((s) => s.items.length > 0),
    [query, section]
  );

  const shown = visible.reduce((n, s) => n + s.items.length, 0);

  const renderCard = (project, index) => {
    const clickable = Boolean(project.link) && !project.comingSoon;
    const external = clickable && project.link.startsWith("http");
    return (
      <Card
        key={index}
        className={clickable ? "index-card is-clickable" : "index-card"}
        onClick={clickable ? () => go(project.link) : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  go(project.link);
                }
              }
            : undefined
        }
        tabIndex={clickable ? 0 : undefined}
        role={clickable ? "link" : undefined}
      >
        <Card.Content>
          <Card.Header
            style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
          >
            <span style={{ flex: 1, marginRight: "0.5em" }}>{project.title}</span>
            {clickable && (
              <Icon
                name={external ? "external alternate" : "play circle"}
                color="blue"
                style={{ flexShrink: 0, marginTop: "2px" }}
              />
            )}
          </Card.Header>

          <Card.Meta style={{ marginTop: "0.35em", fontSize: "0.8rem", color: "#888" }}>
            {project.tech.join(" · ")}
          </Card.Meta>

          <Card.Description style={{ marginTop: "0.75em", lineHeight: "1.55", fontSize: "0.9rem" }}>
            {project.description}
          </Card.Description>

          {project.images && (
            <div className="project-images-strip" style={{ marginTop: "0.75em" }}>
              {project.images.map((img, i) => (
                <img
                  key={i}
                  src={img.src}
                  alt={img.alt}
                  onClick={(e) => {
                    // Thumbnails open the lightbox without also launching the
                    // card's link.
                    e.stopPropagation();
                    setSelectedImage(img);
                  }}
                />
              ))}
            </div>
          )}
        </Card.Content>

        {project.comingSoon && (
          <Card.Content extra>
            <Icon name="clock outline" />
            Coming Soon
          </Card.Content>
        )}
        {clickable && (
          <Card.Content extra>
            <span className="card-launch-link">
              <Icon name="external alternate" />
              {external ? "Visit" : "Launch"}
            </span>
          </Card.Content>
        )}
      </Card>
    );
  };

  return (
    <Container style={{ padding: "1.5em 0 3em" }}>
      <div className="index-header">
        <h1 className="index-title">Everything I've built</h1>
        <p className="index-sub">
          {TOTAL} apps, tools, games and experiments — most run right in the browser, so you can
          click into any of them from here.
        </p>
      </div>

      <div className="index-toolbar">
        <div className="index-search">
          <Icon name="search" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, description, or tech…"
            aria-label="Search projects"
          />
          {query && (
            <button className="index-clear" onClick={() => setQuery("")} aria-label="Clear search">
              ×
            </button>
          )}
        </div>

        <div className="index-chips">
          <button
            className={`index-chip${section === "all" ? " on" : ""}`}
            onClick={() => setSection("all")}
          >
            All <span className="index-count">{TOTAL}</span>
          </button>
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              className={`index-chip${section === s.key ? " on" : ""}`}
              onClick={() => setSection(s.key)}
            >
              {s.label} <span className="index-count">{s.items.length}</span>
            </button>
          ))}
        </div>
      </div>

      {query && (
        <div className="index-results">
          {shown} {shown === 1 ? "match" : "matches"} for “{query}”
        </div>
      )}

      {visible.map((s) => (
        <section key={s.key}>
          <div className="projects-section-title">
            {s.label}
            <span className="index-blurb">{s.blurb}</span>
          </div>
          <div className={s.key === "apps" ? "featured-apps-grid" : "projects-grid"}>
            {s.items.map((p, i) => renderCard(p, i))}
          </div>
        </section>
      ))}

      {visible.length === 0 && (
        <div className="index-empty">
          <Icon name="search" size="big" />
          <p>
            Nothing matches “{query}”.{" "}
            <button className="index-linkish" onClick={() => setQuery("")}>
              Clear the search
            </button>
          </p>
        </div>
      )}

      <Modal
        open={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        size="large"
        closeIcon
      >
        <Modal.Content image>
          <Image
            src={selectedImage?.src}
            alt={selectedImage?.alt}
            style={{ maxHeight: "80vh", width: "auto", margin: "0 auto" }}
          />
        </Modal.Content>
      </Modal>
    </Container>
  );
};

export default Projects;
